import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { safeFetchJSON } from "../lib/safeFetch";

const SiteSettingsContext = createContext({ settings: {}, liveChat: {}, telegramSupport: {}, loading: true, refresh: () => {} });

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [liveChat, setLiveChat] = useState({ enabled: false, show_header_button: true, show_floating_button: true, ai_enabled: true, position: "bottom-right" });
  const [telegramSupport, setTelegramSupport] = useState({ enabled: false, url: "", label: "Telegram Support", tagline: "", open_in_new_tab: true });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const [s, lc, tg] = await Promise.all([
        safeFetchJSON(`${BACKEND_URL}/api/public/site-settings`, { cache: "no-store" }).then((d) => d || {}),
        safeFetchJSON(`${BACKEND_URL}/api/public/live-chat`, { cache: "no-store" }).then((d) => d || {}),
        safeFetchJSON(`${BACKEND_URL}/api/public/telegram-support`, { cache: "no-store" }).then((d) => d || {}),
      ]);
      if (s) setSettings(s);
      if (lc) setLiveChat({
        enabled: !!lc.enabled,
        provider: lc.provider || "",
        position: lc.position || "bottom-right",
        show_header_button: lc.show_header_button !== false,
        show_floating_button: lc.show_floating_button !== false,
        ai_enabled: lc.ai_enabled !== false,
      });
      if (tg) setTelegramSupport({
        enabled: !!tg.enabled,
        url: tg.url || "",
        label: tg.label || "Telegram Support",
        tagline: tg.tagline || "",
        open_in_new_tab: tg.open_in_new_tab !== false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchAll();
    void api;

    // Auto-refresh removed (per user request): no visibility / focus refetch.
    // Site settings (logos, support URLs, telegram links) rarely change during
    // a single user session, and refetching on every tab return was part of
    // the "page keeps refreshing by itself" complaint. Settings update on next
    // hard navigation or reload — perfectly acceptable.

    return () => {
      cancelled = true;
      void cancelled;
    };
  }, [fetchAll]);

  return (
    <SiteSettingsContext.Provider value={{ settings, liveChat, telegramSupport, loading, refresh: fetchAll }}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings() {
  return useContext(SiteSettingsContext);
}

/**
 * Tries to open the currently-active 3rd-party live chat widget, if any.
 * Falls through to a no-op if no provider is loaded.
 */
export function openLiveChatWidget() {
  try {
    if (typeof window === "undefined") return false;
    // Tawk.to
    if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
      window.Tawk_API.maximize();
      return true;
    }
    // Crisp
    if (window.$crisp && typeof window.$crisp.push === "function") {
      window.$crisp.push(["do", "chat:open"]);
      return true;
    }
    // Tidio
    if (window.tidioChatApi && typeof window.tidioChatApi.open === "function") {
      window.tidioChatApi.open();
      return true;
    }
    // JivoChat
    if (window.jivo_api && typeof window.jivo_api.open === "function") {
      window.jivo_api.open();
      return true;
    }
    // Intercom
    if (typeof window.Intercom === "function") {
      window.Intercom("show");
      return true;
    }
    // Zendesk
    if (typeof window.zE === "function") {
      try { window.zE("messenger", "open"); return true; } catch (_) {}
      try { window.zE("webWidget", "open"); return true; } catch (_) {}
    }
  } catch (_) {
    // ignore
  }
  return false;
}
